import { create } from 'zustand';
import walletService from '../services/walletService';
import withdrawalService from '../services/withdrawalService';

const useWalletStore = create((set, get) => ({
  // State
  wallets: [],
  balances: {},
  transactions: [],
  deposits: [],
  withdrawals: [],
  isLoading: false,
  error: null,

  // Actions
  fetchWallets: async () => {
    set({ isLoading: true, error: null });
    try {
      const wallets = await walletService.getWallets();
      set({ wallets, isLoading: false });
      return wallets;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  fetchBalances: async () => {
    set({ isLoading: true, error: null });
    try {
      const balances = await walletService.getBalances();
      set({ balances: balances || {}, isLoading: false });
      return balances || {};
    } catch (error) {
      console.error('Error fetching wallet balances:', error);
      set({ error: error.message, balances: {}, isLoading: false });
      return {};
    }
  },

  fetchTransactions: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const transactions = await walletService.getWalletHistory(params);
      set({ transactions, isLoading: false });
      return transactions;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  fetchDeposits: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const deposits = await walletService.getDeposits(params);
      set({ deposits, isLoading: false });
      return deposits;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  fetchWithdrawals: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const withdrawals = await walletService.getWithdrawals(params);
      set({ withdrawals, isLoading: false });
      return withdrawals;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  createWithdrawal: async (withdrawalData) => {
    set({ isLoading: true, error: null });
    try {
      const result = await withdrawalService.createWithdrawal(withdrawalData);
      // Refresh balances and withdrawals after creating withdrawal
      get().fetchBalances();
      get().fetchWithdrawals();
      set({ isLoading: false });
      return result;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  createWallet: async (currency) => {
    set({ isLoading: true, error: null });
    try {
      const result = await walletService.createWallet(currency);
      // Refresh wallets after creating new wallet
      get().fetchWallets();
      set({ isLoading: false });
      return result;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Fetch withdrawal networks for a specific currency
  fetchWithdrawalNetworks: async (currency) => {
    try {
      const networks = await withdrawalService.getWithdrawalNetworks(currency);
      return networks;
    } catch (error) {
      console.error('Error fetching withdrawal networks:', error);
      throw error;
    }
  },

  // Fetch withdrawal fees for a specific currency and network
  fetchWithdrawalFees: async (currency, network) => {
    try {
      const fees = await withdrawalService.getWithdrawalFees(currency, network);
      return fees;
    } catch (error) {
      console.error('Error fetching withdrawal fees:', error);
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  // Utility functions
  getBalance: (currency) => {
    const { balances } = get();
    return balances[currency] || { actual: 0, orders: 0 };
  },

  getTotalBalance: (currency = 'USD') => {
    const { balances } = get();
    // Calculate total USD value from all balances (sum of actual_usd)
    return Object.values(balances).reduce((total, balance) => {
      return total + (balance.usdValue || 0); // usdValue is mapped from actual_usd
    }, 0);
  },

  // Transfer functions
  validateRecipient: async (recipientEmail) => {
    try {
      const result = await walletService.validateRecipient(recipientEmail);
      return result;
    } catch (error) {
      console.error('Error validating recipient:', error);
      throw error;
    }
  },

  createInternalTransfer: async (transferData) => {
    set({ isLoading: true, error: null });
    try {
      const result = await walletService.createInternalTransfer(transferData);
      // Refresh balances after transfer
      get().fetchBalances();
      set({ isLoading: false });
      return result;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  fetchTransferHistory: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const history = await walletService.getTransferHistory(params);
      set({ isLoading: false });
      return history;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },
}));

export default useWalletStore; 